import { Divider } from 'antd';
import Card from 'antd/es/card/Card';
import Flex from 'antd/es/flex';
import Text from 'antd/es/typography/Text';
import Title from 'antd/es/typography/Title';
import { FormikErrors, FormikValues, useFormik } from 'formik';
import { FC } from 'react';
import * as Yup from 'yup';
import { MButton, MInput } from '@Components/Base';
import Modal from 'antd/es/modal/Modal';
import {
  MaterialDto,
  TenderSupplier,
  VendorOfferDto,
} from '../../ComparisonSheet.types';
import styles from './VendorQuantitySplit.module.css';

export type VendorQuantitySplitFormValues = {
  offers: Array<{
    vendorId: number;
    quantity: number;
  }>;
};
export interface VendorQuantitySplitProps {
  info: {
    material: MaterialDto;
    offers: Array<{
      vendor: TenderSupplier;
      offer: VendorOfferDto;
    }>;
  };
  onSubmit?: (values: VendorQuantitySplitFormValues) => void;
  onCancel?: () => void;
}

const VendorQtySchema = Yup.object().shape({
  offers: Yup.array()
    .of(
      Yup.object().shape({
        vendorId: Yup.number(),
        quantity: Yup.number()
          .min(0, 'Must be at least 0')
          .max(100, "Can't be more than 100")
          .required('Required'),
      })
    )
    .test('Quantity check', 'Total quantity must be 100 %', (items) => {
      if (!items) return false;
      const total = items.reduce((sum, item) => sum + Number(item.quantity), 0);
      return total === 100;
    }),
});

const VendorQuantitySplit: FC<VendorQuantitySplitProps> = ({
  info,
  onSubmit,
  onCancel,
}) => {
  const { material, offers } = info;

  const initialValuesForm: VendorQuantitySplitFormValues = {
    offers: offers.map((o) => ({ vendorId: o.vendor.id, quantity: 0 })),
  };

  const validate = (values: FormikValues): FormikErrors<FormikValues> => {
    try {
      VendorQtySchema.validateSync(values, { abortEarly: true });
      return {};
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      // show error under one key
      return { offers: error.message };
    }
  };

  const formik = useFormik({
    initialValues: initialValuesForm,
    onSubmit: (values: VendorQuantitySplitFormValues) => onSubmit?.(values),
    validateOnChange: true,
    validate,
    validationSchema: VendorQtySchema,
  });

  const customButtons = (
    <div className={styles.footerContainer}>
      <Flex justify="stretch" gap="small" className={styles.footer}>
        <MButton
          onClick={() => {
            onCancel?.();
          }}
          type="tertiary"
        >
          Cancel
        </MButton>
        <MButton
          type="primary"
          onClick={() => {
            formik.submitForm();
          }}
        >
          Save changes
        </MButton>
      </Flex>
    </div>
  );

  return (
    <Modal
      open={!!info}
      className={styles.modal}
      footer={customButtons}
      width={600}
      title="Vendor Quantity Split"
      onCancel={() => {
        onCancel?.();
      }}
    >
      <Card className={styles['meterial-block']}>
        <Flex vertical gap="small">
          <Flex gap="small">
            <Text strong>Material No. {material.code}</Text>
          </Flex>
          <Flex gap="small">
            <Text>{material.description}</Text>
          </Flex>
        </Flex>
        <Divider />
        <Flex vertical gap="small">
          <Flex gap="small">
            <Text type="secondary">Unit</Text>
            <Text strong>{material.unit}</Text>
          </Flex>
          <Flex gap="small">
            <Text type="secondary">Required Qty</Text>
            <Text strong>{material.requiredQty}</Text>
          </Flex>
        </Flex>
      </Card>
      <div>
        <Flex vertical gap="small">
          <Flex>
            <Title level={5}>Vendors</Title>
          </Flex>
          <Flex gap="24px 8px" wrap="wrap">
            {offers.map((offer, offerIndex) => {
              return (
                <Flex
                  vertical
                  gap="small"
                  flex="0 1 calc(50% - 8px)"
                  key={offer.vendor.id}
                >
                  <Text className={styles.vendorName}>{offer.vendor.name}</Text>
                  <Text type="secondary">Quantity %</Text>
                  <MInput
                    name={`offers[${offerIndex}].quantity`}
                    onChange={(e) => {
                      formik.setFieldValue(
                        `offers[${offerIndex}].quantity`,
                        e.target.value
                      );
                    }}
                    value={formik.values?.offers[offerIndex].quantity}
                    placeholder="Enter Percentage"
                  />
                  {formik.errors.offers && (
                    <div style={{ color: 'red', marginBottom: '1em' }}>
                      {formik.errors.offers as string}
                    </div>
                  )}
                </Flex>
              );
            })}
          </Flex>
        </Flex>
      </div>
    </Modal>
  );
};

export default VendorQuantitySplit;
